'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { Play, AlertCircle, X } from 'lucide-react';
import Spinner from '@/components/Spinner';
import { useDashboardData } from '@/lib/hooks/useDashboardData';
import { useUiPreferences } from '@/lib/hooks/useUiPreferences';
import { isAreaEnabled } from '@/lib/uiPreferences';
import LeanDashboard from '@/components/dashboard/personas/LeanDashboard';
import OperatorDashboard from '@/components/dashboard/personas/OperatorDashboard';
import TacticalDashboard from '@/components/dashboard/personas/TacticalDashboard';
import HypertrophyDashboard from '@/components/dashboard/personas/HypertrophyDashboard';
import BiohackerDashboard from '@/components/dashboard/personas/BiohackerDashboard';
import ErrorBoundary from '@/components/ErrorBoundary';
import ProgramComplete, { getProgramDurationWeeks } from '@/components/dashboard/ProgramComplete';
import ProgramDashboard from '@/components/dashboard/ProgramDashboard';
import FreestyleDashboard from '@/components/dashboard/FreestyleDashboard';
import GettingStarted from '@/components/dashboard/GettingStarted';
import SharedDashboardTail from '@/components/dashboard/SharedDashboardTail';
import PostWorkoutImpact from '@/components/dashboard/PostWorkoutImpact';
import ReentryBanner from '@/components/dashboard/ReentryBanner';
import HomeReadinessCard from '@/components/dashboard/HomeReadinessCard';
import DeloadBanner from '@/components/dashboard/DeloadBanner';
import WearableReauthBanner from '@/components/WearableReauthBanner';
import AlertStack from '@/components/dashboard/AlertStack';
import { storage } from '@/lib/storage';

// Conditionally-rendered chunks: deferred to keep the initial bundle lean.
// `loading: () => null` because each is gated by a state flag — the absence
// while the chunk loads is indistinguishable from the gated-off state.
const RevenueCatPaywall = dynamic(() => import('@/components/RevenueCatPaywall'), { ssr: false, loading: () => null });
const ArchetypeUnlockCeremony = dynamic(() => import('@/components/ArchetypeUnlockCeremony'), { ssr: false, loading: () => null });
const ProgramPromptBanner = dynamic(() => import('@/components/ProgramPromptBanner'), { ssr: false, loading: () => null });
const TrialBanner = dynamic(() => import('@/components/TrialBanner'), { ssr: false, loading: () => null });
const WinBackBanner = dynamic(() => import('@/components/WinBackBanner'), { ssr: false, loading: () => null });
const ProgramNavigator = dynamic(() => import('@/components/ProgramNavigator'), { ssr: false, loading: () => null });
const WelcomeTour = dynamic(() => import('@/components/WelcomeTour'), { ssr: false, loading: () => null });
const FirstWinsChecklist = dynamic(() => import('@/components/FirstWinsChecklist'), { ssr: false, loading: () => null });
const TacticalAlert = dynamic(() => import('@/components/dashboard/TacticalAlert'), { ssr: false, loading: () => null });
// Pillar Mission card — only loaded for users with the
// featureFlags.pillarChallenges flag on their profile. Lazy because
// most users won't have an active challenge.
const PillarMissionCard = dynamic(() => import('@/components/dashboard/PillarMissionCard'), { ssr: false, loading: () => null });
// Restart gate — auto-fires when yesterday's challenge day was failed.
// Lives at the dashboard root so the decision can't be dismissed by
// routing away. Self-gates: no challenge → renders null.
const RestartGate = dynamic(() => import('@/components/challenge/RestartGate'), { ssr: false, loading: () => null });
// Adaptive personal-model banner — self-gates on confidence + signal,
// renders only when the user has a meaningful deviation from baseline.
const PersonalStateBanner = dynamic(() => import('@/components/dashboard/PersonalStateBanner'), { ssr: false, loading: () => null });
// Z2 base accumulator — weekly aerobic-zone minutes vs program target.
// Self-gates: renders only when there's HR-zone data in the last 7 days.
const Z2BaseCard = dynamic(() => import('@/components/dashboard/Z2BaseCard'), { ssr: false, loading: () => null });
// Recovery Quality — daily counterpart to morning readiness. Pure
// overnight signal (HRV + sleep + RHR). Self-gates on confidence ≥0.30.
const RecoveryQualityCard = dynamic(() => import('@/components/dashboard/RecoveryQualityCard'), { ssr: false, loading: () => null });
// Cardiac age — RHR + HRV baselines mapped to biological cardiovascular
// age. Self-gates on baseline confidence ≥0.5 (≥14 days needed).
const CardiacAgeCard = dynamic(() => import('@/components/dashboard/CardiacAgeCard'), { ssr: false, loading: () => null });
// Travel / injury break banner. Rendered above the persona switch so EVERY
// persona shows it — see the comment at its render site.
const TrainingBreakBanner = dynamic(() => import('@/components/TrainingBreakBanner'), { ssr: false, loading: () => null });

function HomeInner() {
  const data = useDashboardData();
  const { prefs } = useUiPreferences();
  // Bespoke persona bodies own their own above-the-fold composition.
  // Lean = minimal (workout + fuel + 1-line recovery), owns the whole
  // body incl. tail. Operator = GO-light + Deploy, then keeps the shared
  // tail (ops/missions/week). Both fold recovery into their own hero, so
  // the Full-layout recovery cluster + readiness card are suppressed for
  // both; the shared tail is suppressed for Lean only.
  const isLean = prefs.persona === 'lean';
  const isOperator = prefs.persona === 'operator';
  const isTactical = prefs.persona === 'tactical';
  const isHypertrophy = prefs.persona === 'hypertrophy';
  const isBiohacker = prefs.persona === 'biohacker';
  // All bespoke personas own their own readiness/recovery hero, so the
  // Full-layout recovery cluster + readiness card are suppressed for them.
  const isBespoke = isLean || isOperator || isTactical || isHypertrophy || isBiohacker;
  // The shared tail (ops/missions/week/vitals/AI brief) stays for Full +
  // Operator (operator wants the ops content); the self-contained
  // bodies (Lean / Tactical / Hypertrophy / Biohacker) suppress it.
  const showTail = !isLean && !isTactical && !isHypertrophy && !isBiohacker;
  // Discard-workout confirmation modal. Replaces a native confirm() that
  // (a) showed Android Capacitor's "https://opmode.app says…" chrome and
  // (b) was paired with a discard handler that only cleared V1 backup
  // keys, leaving the V2 backup to re-hydrate on next mount. See related
  // fix in app/session/page.tsx handleStartFresh.
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [discarding, setDiscarding] = useState(false);

  const performDiscard = async () => {
    if (discarding) return;
    setDiscarding(true);
    try {
      // Cancel any pending debounced cloud save FIRST — otherwise it'll
      // fire 60s later and re-create the doc we're about to delete,
      // making the discard look broken. The 401 the user saw in console
      // was that timer firing against a stale auth token.
      try {
        const { cancelActiveWorkoutCloudSave } = await import('@/lib/sessionStorage');
        cancelActiveWorkoutCloudSave();
      } catch {}
      storage.remove('activeSessionBackup');
      storage.remove('activeSessionBackupV2');
      storage.remove('freestyleSessionBackup');
      const userId = data.user?.uid;
      if (userId) {
        try {
          const { clearActiveWorkout } = await import('@/lib/db');
          await clearActiveWorkout(userId, 'pt');
          await clearActiveWorkout(userId, 'pt-v2');
          await clearActiveWorkout(userId, 'freestyle');
        } catch (e) { console.warn('Dexie discard failed:', e); }
        try {
          const { clearActiveWorkoutFromCloud } = await import('@/lib/sessionStorage');
          await clearActiveWorkoutFromCloud(userId);
        } catch (e) { console.warn('Firestore discard failed:', e); }
      }
      // Signal the dashboard hook to drop its activeSession state
      // immediately — router.refresh() alone doesn't re-run client
      // hooks, so without this the "Workout in Progress" card lingers
      // even though storage is cleared.
      window.dispatchEvent(new CustomEvent('sessionDiscarded'));
      setShowDiscardConfirm(false);
      data.router.refresh();
    } finally {
      setDiscarding(false);
    }
  };

  // Welcome Tour — fired once for new users right after onboarding. Read
  // `users/{uid}.tourCompleted` from Firestore on mount; if missing/false
  // and the user has finished onboarding, render the 4-step modal.
  // `?tour=1` query param forces a replay (useful for testing or for
  // users who want to revisit the intake brief). Dismiss writes
  // `tourCompleted: true` so the normal path never re-fires.
  const [showTour, setShowTour] = useState(false);
  useEffect(() => {
    if (!data.user) return;
    let cancelled = false;
    const forceReplay = typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('tour') === '1';
    (async () => {
      try {
        if (forceReplay) {
          if (!cancelled) setShowTour(true);
          return;
        }
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/lib/firebase');
        const snap = await getDoc(doc(db, 'users', data.user!.uid));
        if (cancelled || !snap.exists()) return;
        const u = snap.data();
        if (u?.onboardingComplete && !u?.tourCompleted) {
          setShowTour(true);
        }
      } catch { /* best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [data.user]);

  const dismissTour = async () => {
    setShowTour(false);
    if (!data.user) return;
    // Strip `?tour=1` from the URL so reloading doesn't loop the tour.
    if (typeof window !== 'undefined' && window.location.search.includes('tour=1')) {
      const url = new URL(window.location.href);
      url.searchParams.delete('tour');
      window.history.replaceState({}, '', url.toString());
    }
    try {
      const { doc, setDoc } = await import('firebase/firestore');
      const { db } = await import('@/lib/firebase');
      await setDoc(doc(db, 'users', data.user.uid), { tourCompleted: true }, { merge: true });
    } catch { /* best-effort — worst case the tour re-fires once */ }
  };

  // Loading state
  if (data.loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Spinner size={48} label="Loading..." />
      </div>
    );
  }

  // Determine dashboard mode
  const isProgramComplete = data.programSelection
    ? data.currentWeek > getProgramDurationWeeks(data.programSelection.programId)
    : false;

  return (
    <div className="space-y-4 pb-24 sm:pb-28">
      {/* Wearable reauth banner — shows only when a provider has needsReauth=true */}
      <WearableReauthBanner />

      {/* Reentry banner — welcome-back / drift / fresh-start. Renders only when
          status is not "on-track", so it stays out of the way during normal use. */}
      <ReentryBanner state={data.reentryState} />

      {/* Deload banner — fires when the engine flags fatigue accumulation.
          Suppressed during reentry (a returning user shouldn't deload further). */}
      {data.user && (
        <DeloadBanner intelligence={data.intelligenceReport} reentry={data.reentryState} />
      )}

      {/* Active Session Resume — promoted above readiness/gating because the user
          has an intent in progress; everything else is context. Cyan = in-flight. */}
      {data.activeSession && (
        <section className="relative overflow-hidden rounded-xl border border-hud-cyan/40 bg-hud-ink/85 shadow-[0_0_40px_-16px_rgba(103,232,249,0.4)]">
          <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-hud-cyan/80 to-transparent" />
          <span aria-hidden className="pointer-events-none absolute top-0 left-0 w-3 h-3 border-t border-l border-hud-cyan/60" />
          <span aria-hidden className="pointer-events-none absolute top-0 right-0 w-3 h-3 border-t border-r border-hud-cyan/60" />
          <span aria-hidden className="pointer-events-none absolute bottom-0 left-0 w-3 h-3 border-b border-l border-hud-cyan/60" />
          <span aria-hidden className="pointer-events-none absolute bottom-0 right-0 w-3 h-3 border-b border-r border-hud-cyan/60" />
          <span aria-hidden className="absolute top-0 bottom-0 left-0 w-1 bg-hud-cyan" />

          <header className="relative flex items-center gap-2 px-4 py-1.5 border-b border-hud-cyan/20 font-mono text-[9px] uppercase tracking-[0.28em] text-hud-cyan">
            <span className="w-1 h-1 rounded-full bg-hud-cyan animate-hud-blink" aria-hidden />
            <span>Workout In Progress</span>
          </header>

          <div className="px-4 pt-3 pb-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 rounded-md border border-hud-cyan/40 bg-hud-cyan/10 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-hud-cyan" />
              </div>
              <div className="min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-hud-cyan font-bold">
                  {data.activeSession.type === 'program' ? 'Workout · In Progress' : 'Free Lift · In Progress'}
                </div>
                <div className="font-mono text-[11px] text-slate-400 mt-1 tabular-nums">
                  <span className="text-slate-200">{data.activeSession.exerciseCount}</span> exercise{data.activeSession.exerciseCount !== 1 ? 's' : ''} tracked
                  {data.activeSession.duration ? <> <span className="text-slate-700">·</span> <span className="text-slate-200">{Math.floor(data.activeSession.duration / 60)}m</span> elapsed</> : null}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Link
                href={data.activeSession.type === 'program' ? '/session' : '/session/freestyle'}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-hud-cyan text-operator-void font-mono font-bold tracking-[0.18em] text-[11px] uppercase hover:bg-cyan-300 transition shadow-[0_0_18px_-6px_rgba(103,232,249,0.7)]"
              >
                <Play className="w-3.5 h-3.5" />
                Resume
              </Link>
              <button
                onClick={() => setShowDiscardConfirm(true)}
                className="p-2 text-slate-600 hover:text-red-400 transition"
                title="Discard workout"
                aria-label="Discard workout"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Pillar Challenge mission card — sits at the very top of the
          dashboard when the user has an active challenge AND the
          featureFlags.pillarChallenges flag is on (or they're admin).
          Self-gates: renders null otherwise, no cost to non-challenge
          users. Today's commitments, pillar breakdown, why-statement,
          streak ribbon. Tap any auto commitment → navigates to source. */}
      {/* Tactical renders its readiness console FIRST, then the challenge
          card below the body (see after the persona block) — so the
          deployment status leads instead of being buried under the full
          Sober-30-style checklist. Every other persona keeps it on top. */}
      {data.user && isAreaEnabled(prefs, 'challenges') && !isTactical && <PillarMissionCard />}
      {data.user && isAreaEnabled(prefs, 'challenges') && <RestartGate />}

      {/* Adaptive personal-model deviation banner. Sits ABOVE the
          readiness card because it explains the WHY behind the score
          (HRV down X%, sleep debt accumulating, etc.) — and because
          it's the smarter long-term coaching artifact: it learns from
          THIS user's data, not population thresholds. Self-gates: only
          shows when there's meaningful signal AND enough confidence. */}
      {data.user && !isBespoke && isAreaEnabled(prefs, 'recovery') && <PersonalStateBanner />}

      {/* Recovery Quality — overnight signal only. Surfaces above the
          Z2 / readiness cards because it answers the most immediate
          morning question ("did I recover?"). Self-gates on confidence. */}
      {data.user && !isBespoke && isAreaEnabled(prefs, 'recovery') && <RecoveryQualityCard />}

      {/* Z2 base — weekly aerobic-zone minutes accumulator. Reads HR
          samples from BLE in-workout + wearable daily HR series. When
          program metadata defines a weeklyZ2Target, shows progress
          against it. Self-gates when no HR data exists in the window. */}
      {data.user && !isBespoke && isAreaEnabled(prefs, 'recovery') && <Z2BaseCard />}

      {/* Cardiac age — long-arc KPI. Surfaces below the daily cards
          because it's a trending number that changes monthly, not
          daily. Self-gates on baseline confidence. Wrapped in its own
          ErrorBoundary so a render-time issue (e.g. malformed baseline
          shape from a stale cache) can't take down the rest of the
          dashboard — a card going dark is acceptable; the home screen
          going white is not. */}
      {data.user && !isBespoke && isAreaEnabled(prefs, 'recovery') && (
        <ErrorBoundary fallbackTitle="Cardiac age unavailable">
          <CardiacAgeCard />
        </ErrorBoundary>
      )}

      {/* Compact readiness card — Go / Caution / Rest at a glance with the
          tailored Today's Orders one-liner. Tap → /metrics for full SITREP.
          Gated for non-Pro users who've consumed their preview: the score
          itself is wearable-derived insight, which is the chief value prop
          for paid plans. Raw data (HRV / sleep / steps) stays visible. */}
      {/* Operator surfaces readiness via its GO light; Tactical via the
          Physical pillar of the readiness console; Lean drops it to a
          single line. Hide this card for those personas so the same
          number doesn't show twice. */}
      {data.user && !isLean && !isOperator && !isTactical && (
        <HomeReadinessCard
          intelligence={data.intelligenceReport}
          gated={!data.isPro && !data.isBeta && !data.hasPreviewAccess && !data.trialInfo.isInTrial}
        />
      )}

      {/* Secondary alert stack — onboarding + conversion banners.
          Showed up to 3 stacked for a new user before this. Now the
          most urgent visible one shows full size; the rest collapse
          behind a "X more alerts" disclosure. The Trial/WinBack/
          Preview/Gate group is already mutually exclusive (only one
          fires based on user state), so the realistic stack is
          conversion-banner + FirstWins + ProgramPrompt. */}
      {data.user && (
        <AlertStack
          alerts={[
            {
              key: 'gate',
              priority: 80,
              visible: !data.isPro && !data.isBeta && !data.trialInfo.isInTrial && !data.isLoadingProAccess && !data.hasPreviewAccess,
              render: () => (
                <TacticalAlert
                  severity="gate"
                  badge="Access · Locked"
                  title="Unlock OPMODE Pro"
                  body="You've completed your preview. Start your 14-day (monthly) or 30-day (annual) free trial to keep training, unlock adaptive coaching, nutrition tracking, and advanced analytics."
                  cta={{ label: 'Start Free Trial', onClick: () => data.setShowProgramPaywall(true) }}
                  footer="No charge during trial · Cancel anytime"
                />
              ),
            },
            {
              key: 'trial',
              priority: 70,
              visible: !data.isBeta && data.trialInfo.isInTrial,
              render: () => (
                <TrialBanner trialInfo={data.trialInfo} onSubscribe={() => data.setShowProgramPaywall(true)} />
              ),
            },
            {
              key: 'winback',
              priority: 65,
              visible: !data.isPro && !data.isBeta && !data.trialInfo.isInTrial && !data.hasPreviewAccess && data.lapsedTrialInfo.hadEntitlement,
              render: () => (
                <WinBackBanner lapsedTrialInfo={data.lapsedTrialInfo} onSubscribe={() => data.setShowProgramPaywall(true)} />
              ),
            },
            {
              key: 'preview',
              priority: 60,
              visible: !data.isPro && !data.isBeta && !data.trialInfo.isInTrial && !data.isLoadingProAccess && data.hasPreviewAccess,
              render: () => (
                <TacticalAlert
                  severity="active"
                  badge="Preview Active"
                  title="Your first training session is on us"
                  body="Run one full session to feel the auto-adjusted loads and the post-workout impact. Calibration tests don't count — they're always free."
                />
              ),
            },
            {
              key: 'first-wins',
              priority: 40,
              visible: !showTour,
              render: () => <FirstWinsChecklist />,
            },
          ]}
        />
      )}

      {/* Toast Notification */}
      {data.showToast && (
        <div className="fixed top-4 right-4 z-50 bg-operator-gold text-operator-void px-6 py-3 rounded-lg shadow-gold-lg font-bold uppercase animate-slide-in-right">
          Success!
        </div>
      )}

      {/* Post-Workout Impact (Intel earned + Spearhead territory gains) */}
      <PostWorkoutImpact />

      {/* Program Prompt Banner */}
      {data.showProgramPrompt && data.user && (
        <ProgramPromptBanner
          userId={data.user.uid}
          onDismiss={() => data.setShowProgramPrompt(false)}
        />
      )}

      {/* Program Completion Celebration */}
      {isProgramComplete && data.programSelection && data.user && (
        <ProgramComplete
          userId={data.user.uid}
          durationWeeks={getProgramDurationWeeks(data.programSelection.programId)}
        />
      )}

      {/* Training Break Banner (Travel Mode / Injury Recovery).

          Hoisted ABOVE the persona switch on purpose. It used to live inside
          ProgramDashboard, so only that one persona rendered it — while every
          persona hides the Start Workout CTA on an active break. The other
          five therefore lost their primary action with nothing in its place,
          no explanation, and no way to end the trip. This is also the only
          route to the travel workouts. */}
      {data.activeBreak && data.user && (
        <TrainingBreakBanner
          breakData={data.activeBreak}
          onEndBreak={data.handleEndBreak}
        />
      )}

      {/* MODE: Program Dashboard — persona-bespoke body. Lean = minimal,
          Tactical = readiness console, Operator = GO-light + Deploy,
          Hypertrophy = volume/physique, everyone else = full. */}
      {data.programSelection && !isProgramComplete && !data.showProgramPrompt && data.user && (
        isLean ? <LeanDashboard data={data} />
        : isTactical ? <TacticalDashboard data={data} />
        : isHypertrophy ? <HypertrophyDashboard data={data} />
        : isBiohacker ? <BiohackerDashboard data={data} />
        : isOperator ? <OperatorDashboard data={data} />
        : <ProgramDashboard data={data} />
      )}

      {/* Tactical's challenge card — rendered AFTER the readiness console
          so the deployment status + pillars lead, with the daily-discipline
          checklist following. (Other personas render it at the top.) */}
      {data.user && isTactical && isAreaEnabled(prefs, 'challenges') &&
        data.programSelection && !isProgramComplete && !data.showProgramPrompt && (
        <div className="mt-3"><PillarMissionCard /></div>
      )}

      {/* MODE: Freestyle Dashboard */}
      {!data.programSelection && !data.showProgramPrompt && data.user && data.userPath === 'freestyle' && (
        <FreestyleDashboard data={data} />
      )}

      {/* MODE: Getting Started */}
      {!data.programSelection && !data.showProgramPrompt && data.user && data.userPath !== 'freestyle' && (
        <GettingStarted />
      )}

      {/* Shared tail sections (vitals, operations, workouts, AI brief, quick
          actions). Kept for Full + Operator; the self-contained bespoke
          bodies (Lean / Tactical / Hypertrophy) suppress it. */}
      {data.user && showTail && <SharedDashboardTail data={data} />}

      {/* Program Navigator - Slide-up sheet for day/week navigation */}
      {data.user && data.programSelection && (
        <ProgramNavigator
          userId={data.user.uid}
          selection={data.programSelection}
          isOpen={data.showDayPicker}
          hasRangeDay={false}
          totalWeeks={getProgramDurationWeeks(data.programSelection.programId)}
          onRefresh={data.handleRefreshProgramState}
          onClose={() => data.setShowDayPicker(false)}
        />
      )}

      {/* Program Access Paywall */}
      {data.showProgramPaywall && (
        <RevenueCatPaywall
          onClose={() => data.setShowProgramPaywall(false)}
          onSuccess={() => {
            data.setShowProgramPaywall(false);
            window.location.reload(); // Full reload needed to re-check Pro access at all levels
          }}
          title="Upgrade to OPMODE Pro"
          subtitle="Unlock the full tactical fitness experience"
        />
      )}

      {/* Archetype Unlock Ceremony */}
      <ArchetypeUnlockCeremony />

      {/* Welcome Tour — one-shot click-through right after onboarding. */}
      {showTour && <WelcomeTour onDismiss={dismissTour} />}

      {/* Discard Workout Confirmation — replaces the native confirm() so
          the Android Capacitor browser chrome doesn't leak through and the
          discard can run on a stable async path. */}
      {showDiscardConfirm && (
        <div
          className="fixed inset-0 z-[115] flex items-end sm:items-center justify-center"
          style={{ animation: 'fade-in 0.16s ease-out' }}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-operator-void/85 backdrop-blur-sm"
            onClick={() => !discarding && setShowDiscardConfirm(false)}
          />
          <div className="relative w-full max-w-md bg-operator-void border-t sm:border border-red-500/40 sm:rounded-xl p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
            <h3 className="font-heading text-lg font-bold uppercase text-white mb-2">
              Discard this workout?
            </h3>
            <p className="text-sm text-slate-300 leading-relaxed mb-4">
              Your in-progress sets, weights, and RPE will be deleted from this device and the cloud. This can&apos;t be undone.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setShowDiscardConfirm(false)}
                disabled={discarding}
                className="inline-flex items-center justify-center px-4 py-3 rounded-md border border-operator-steel/40 bg-operator-void/40 hover:bg-operator-gunmetal/60 active:scale-[0.98] disabled:opacity-40 transition font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-slate-300"
              >
                Cancel
              </button>
              <button
                onClick={performDiscard}
                disabled={discarding}
                className="inline-flex items-center justify-center px-4 py-3 rounded-md border border-red-500/60 bg-red-500/15 hover:bg-red-500/25 active:scale-[0.98] disabled:opacity-40 transition font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-red-300"
              >
                {discarding ? 'Discarding…' : 'Discard'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recovery Skip Confirmation — visual language now matches the
          Discard and ConfirmDialog modals (HudPanel chrome, safe-area
          padding, sheet-up animation on mobile). Previously this was a
          one-off bg-operator-slate / border-2 block that looked like it
          came from a different app. */}
      {data.showRecoverySkipConfirm && (
        <div
          className="fixed inset-0 z-[115] flex items-end sm:items-center justify-center"
          style={{ animation: 'fade-in 0.16s ease-out' }}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-operator-void/85 backdrop-blur-sm"
            onClick={() => data.setShowRecoverySkipConfirm(false)}
          />
          <div className="relative w-full max-w-md bg-operator-void border-t sm:border border-operator-gold/40 sm:rounded-xl p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
            <h3 className="font-heading text-lg font-bold uppercase text-white mb-2">
              Recovery not logged
            </h3>
            <p className="text-sm text-slate-300 leading-relaxed mb-4">
              Logging recovery (sleep, soreness, mood) helps the app
              personalize today&apos;s loads. You can still train without it.
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => {
                  data.setShowRecoverySkipConfirm(false);
                  data.router.push('/recovery?from=workout');
                }}
                className="w-full inline-flex items-center justify-center px-4 py-3 rounded-md border border-operator-gold/60 bg-operator-gold/15 hover:bg-operator-gold/25 active:scale-[0.98] transition font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-operator-gold"
              >
                Log Recovery
              </button>
              <button
                onClick={() => {
                  data.setShowRecoverySkipConfirm(false);
                  data.startTodayWorkout(true);
                }}
                className="w-full inline-flex items-center justify-center px-4 py-3 rounded-md border border-operator-steel/40 bg-operator-void/40 hover:bg-operator-gunmetal/60 active:scale-[0.98] transition font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-slate-300"
              >
                Skip &amp; Start Workout
              </button>
              <button
                onClick={() => data.setShowRecoverySkipConfirm(false)}
                className="w-full inline-flex items-center justify-center px-4 py-2 rounded text-xs text-slate-500 hover:text-slate-300 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Wrap HomeInner in an ErrorBoundary so React render-time errors (including
 * minified hydration #300) surface with a decoded console group telling us
 * exactly which component blew up. Without this, the error throws into
 * Next.js's own minified handler and we get nothing actionable.
 */
export default function Home() {
  return (
    <ErrorBoundary fallbackTitle="Dashboard failed to load">
      <HomeInner />
    </ErrorBoundary>
  );
}
